import { useEffect, useRef, useState, type FormEvent } from 'react';
import { isSignInWithEmailLink, signInWithEmailAndPassword, signInWithEmailLink } from 'firebase/auth';
import { mailSentResponseSchema, type MailKind } from '@unit/shared';
import { useTranslation } from 'react-i18next';
import { publicPost } from '../../lib/api.js';
import { auth, usingEmulators } from '../../lib/firebase.js';
import { useRegistrationOpen } from '../registration/useRegistrationOpen.js';
import { fetchEmulatorLink } from './emulatorLink.js';

/** Where a sign-in link lands; the route table sends it home once signed in. */
const LINK_LANDING = '/sign-in-link';
/** The address the link was sent to, so the same browser can finish without retyping it. */
const EMAIL_KEY = 'unit:signInEmail';

const input =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-start outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10';
const primary =
  'w-full rounded-lg bg-slate-900 px-4 py-2.5 font-medium text-white transition hover:bg-slate-800 disabled:opacity-50';

/**
 * Sign in with an emailed link — the first option on the screen — or with a
 * password (product owner, 2026-09-13: link first, password second, and a
 * password-reset link for whoever lost theirs). Either way the session is
 * the whole story: there is no step-up beyond signing in.
 *
 * The emails come from the server (owner, 2026-09-21, story 43: the
 * company's own Gmail mailbox, never Firebase's mail): this screen asks
 * `POST /public/sign-in-link` / `POST /public/password-reset`, and the link
 * that arrives lands back here, on the app's own domain, where the web SDK
 * redeems it.
 */
export function SignIn() {
  const { t } = useTranslation();
  const registrationOpen = useRegistrationOpen();
  const landing = isSignInWithEmailLink(auth, window.location.href) && window.location.pathname === LINK_LANDING;
  const [mode, setMode] = useState<'password' | 'link'>('link');
  const [email, setEmail] = useState(() => readStoredEmail());
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<'signIn' | 'send' | 'link' | 'reset' | null>(null);
  /** Which email the server was asked to send, so the screen can say so and offer the emulator's copy. */
  const [sent, setSent] = useState<MailKind | null>(null);
  const [emulatorMissing, setEmulatorMissing] = useState(false);
  // The link is single-use; StrictMode would otherwise redeem it twice.
  const redeemed = useRef(false);

  const finishWithLink = async (address: string) => {
    setBusy(true);
    setFailed(null);
    try {
      await signInWithEmailLink(auth, address.trim(), window.location.href);
      try {
        localStorage.removeItem(EMAIL_KEY);
      } catch {
        /* storage unavailable: nothing to clear */
      }
      // The route table sends /sign-in-link home once there is a user.
    } catch {
      setFailed('link');
    } finally {
      setBusy(false);
    }
  };

  // Landed here from the link with the address remembered: finish at once.
  // On another device the address is asked for below and the same call runs.
  // `finishWithLink` only touches state setters, so re-creating it per render
  // does not re-run this effect: the ref guards the single-use code.
  useEffect(() => {
    if (!landing || redeemed.current) return;
    const stored = readStoredEmail();
    if (!stored) return;
    redeemed.current = true;
    void finishWithLink(stored);
  });

  async function handlePassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFailed(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch {
      // Deliberately one message for every failure mode. Distinguishing "no
      // such user" from "wrong password" turns the form into an account probe.
      setFailed('signIn');
    } finally {
      setBusy(false);
    }
  }

  async function handleSendLink(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFailed(null);
    try {
      await publicPost('/public/sign-in-link', mailSentResponseSchema, { email: email.trim() });
      try {
        localStorage.setItem(EMAIL_KEY, email.trim());
      } catch {
        /* storage unavailable: the reader will be asked for the address on landing */
      }
      setSent('sign_in');
    } catch {
      // Same one message whether the address is unknown or the send failed:
      // "no such account" would make this form an account probe.
      setFailed('send');
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotPassword() {
    setBusy(true);
    setFailed(null);
    try {
      await publicPost('/public/password-reset', mailSentResponseSchema, { email: email.trim() });
      setSent('password_reset');
    } catch {
      setFailed('reset');
    } finally {
      setBusy(false);
    }
  }

  async function handleLanding(event: FormEvent) {
    event.preventDefault();
    if (redeemed.current) return;
    redeemed.current = true;
    await finishWithLink(email);
    redeemed.current = false;
  }

  const openEmulatorLink = async () => {
    const link = await fetchEmulatorLink(email.trim(), sent ?? 'sign_in');
    if (!link) {
      setEmulatorMissing(true);
      return;
    }
    window.location.assign(link);
  };

  const onSubmit = landing ? handleLanding : mode === 'link' ? handleSendLink : handlePassword;

  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200"
        data-testid="sign-in-form"
        data-mode={landing ? 'landing' : mode}
      >
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-slate-900">{t('app.title')}</h1>
          <p className="text-sm text-slate-500">{landing ? t('auth.linkLandingHint') : t('auth.noSelfRegistration')}</p>
        </header>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-slate-700">{t('auth.email')}</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            dir="ltr"
            className={input}
            disabled={sent !== null}
          />
        </label>

        {mode === 'password' && !landing && sent === null && (
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-slate-700">{t('auth.password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              dir="ltr"
              className={input}
            />
          </label>
        )}

        {failed && (
          <p role="alert" className="text-sm text-red-600" data-testid="sign-in-error">
            {failed === 'signIn'
              ? t('errors.signInFailed')
              : failed === 'send'
                ? t('auth.linkSendFailed')
                : failed === 'reset'
                  ? t('auth.resetFailed')
                  : t('auth.linkInvalid')}
          </p>
        )}

        {sent !== null ? (
          <>
            <p
              className="text-sm text-slate-700"
              data-testid={sent === 'sign_in' ? 'sign-in-link-sent' : 'sign-in-reset-sent'}
            >
              {sent === 'sign_in' ? t('auth.linkSent') : t('auth.resetSent')}
            </p>
            {usingEmulators && (
              <div className="rounded-xl bg-amber-50 p-3 ring-1 ring-amber-200">
                <p className="text-xs text-amber-900">{t('auth.emulatorHint')}</p>
                <button
                  type="button"
                  onClick={() => void openEmulatorLink()}
                  data-testid="sign-in-emulator-link"
                  className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-amber-700"
                >
                  {t('auth.emulatorOpen')}
                </button>
                {emulatorMissing && <p className="mt-2 text-xs text-amber-800">{t('auth.emulatorMissing')}</p>}
              </div>
            )}
          </>
        ) : (
          <button type="submit" disabled={busy} className={primary} data-testid="sign-in-submit">
            {busy
              ? t('auth.signingIn')
              : landing
                ? t('auth.finishWithLink')
                : mode === 'link'
                  ? t('auth.sendLink')
                  : t('auth.signIn')}
          </button>
        )}

        {!landing && sent === null && (
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'link' ? 'password' : 'link');
              setFailed(null);
            }}
            className="w-full text-center text-sm text-slate-600 underline-offset-4 hover:underline"
            data-testid="sign-in-toggle-mode"
          >
            {mode === 'link' ? t('auth.usePassword') : t('auth.useLink')}
          </button>
        )}

        {!landing && sent === null && mode === 'password' && (
          <button
            type="button"
            onClick={() => void handleForgotPassword()}
            disabled={busy || email.trim() === ''}
            className="w-full text-center text-sm text-slate-600 underline-offset-4 hover:underline disabled:opacity-50"
            data-testid="sign-in-forgot"
          >
            {t('auth.forgotPassword')}
          </button>
        )}

        {registrationOpen && !landing && (
          <a href="/register" className="block w-full text-center text-sm text-slate-600 underline-offset-4 hover:underline" data-testid="sign-in-register">
            {t('auth.register')}
          </a>
        )}

        {usingEmulators && !landing && (
          <p className="text-center text-xs text-slate-400">{t('auth.seededAccountsHint')}</p>
        )}
      </form>
    </main>
  );
}

function readStoredEmail(): string {
  try {
    return localStorage.getItem(EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}
